import { EmptyState } from "@/components/empty-state";
import { PageHeader } from "@/components/page-header";
import { TableSkeleton } from "@/components/skeleton";
import { StatusDot } from "@/components/status-dot";
import { type DataTableColumn, DataTableV2 } from "@/components/ui/data-table-v2";
import { ErrorState } from "@/components/ui/error-state";
import { StatusBadge } from "@/components/ui/status-badge";
import { useCurrentProject } from "@/features/projects/current-project";
import { useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { useState } from "react";
import { type ModelSummary, useModels } from "./queries";

const columns: (ColumnDef<ModelSummary, unknown> & DataTableColumn)[] = [
  {
    header: "Name",
    accessorKey: "name",
    cell: ({ row }) => <span className="font-medium">{row.original.name}</span>,
  },
  {
    header: "Task",
    accessorKey: "task_type",
    cell: ({ row }) => <span className="text-fg-muted">{row.original.task_type}</span>,
  },
  {
    header: "Status",
    accessorKey: "status",
    cell: ({ row }) => <StatusBadge status={row.original.status} />,
  },
  {
    header: "Active revision",
    cell: ({ row }) =>
      row.original.active_revision_id ? (
        <span className="inline-flex items-center gap-2 text-xs">
          <StatusDot status="active" label="" />
          <span className="mono text-fg-muted">{row.original.active_revision_id.slice(0, 8)}…</span>
        </span>
      ) : (
        <span className="text-fg-dim">— no active</span>
      ),
  },
  {
    header: "Created",
    accessorKey: "created_at",
    cell: ({ row }) => (
      <span className="mono text-fg-muted">
        {new Date(row.original.created_at).toLocaleString()}
      </span>
    ),
  },
];

export function ModelsListPage() {
  const { projectId } = useCurrentProject();
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const { data, isLoading, isError, refetch } = useModels(projectId, { page, pageSize });
  const navigate = useNavigate();

  if (!projectId) {
    return (
      <>
        <PageHeader
          title="Models"
          subtitle="Trained artifacts · activate a revision to serve inference"
        />
        <EmptyState
          title="No project selected"
          description="Choose a project to view its models."
        />
      </>
    );
  }

  // Backend /v1/models does not paginate today (ignores page/page_size and
  // returns the full list). Slice client-side so DataTableV2's footer text
  // ("Showing X–Y of N") matches the rendered rows. When the backend gains
  // real pagination this slice becomes a no-op.
  const allItems = data?.items ?? [];
  const items =
    allItems.length > pageSize ? allItems.slice((page - 1) * pageSize, page * pageSize) : allItems;
  const total = data?.total ?? allItems.length;

  return (
    <>
      <PageHeader
        title="Models"
        subtitle={`Trained artifacts · activate a revision to serve inference · ${total} models`}
      />
      {isLoading ? (
        <TableSkeleton />
      ) : isError ? (
        <ErrorState message="Failed to load models" onRetry={() => refetch()} />
      ) : (
        <DataTableV2
          data={items}
          columns={columns}
          caption="All models"
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
          onRowClick={(m) => navigate({ to: "/models/$modelId", params: { modelId: m.id } })}
        />
      )}
    </>
  );
}
